import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { getAdminModelStats, getAdminTurnSignals } from '../../api';
import type { AdminModelStat, AdminModelStatsResponse, AdminSignalRate, AdminTurnSignalsResponse } from '../../api';
import { AdminLayout } from '../../components/admin/AdminLayout';

const WINDOW_OPTIONS: { label: string; hours: number }[] = [
  { label: 'Last 1 hour', hours: 1 },
  { label: 'Last 6 hours', hours: 6 },
  { label: 'Last 24 hours', hours: 24 },
  { label: 'Last 3 days', hours: 72 },
  { label: 'Last 7 days', hours: 168 },
  { label: 'Last 30 days', hours: 720 },
];

type SortKey =
  | 'provider'
  | 'model'
  | 'sampleCount'
  | 'successRate'
  | 'avgLatencyMs'
  | 'maxLatencyMs'
  | 'costPerKTokensMicros'
  | 'avgTokensPerRequest'
  | 'totalCostMicros'
  | 'streamedFraction'
  | 'lastSeenAt';

type SortDir = 'asc' | 'desc';

const MIN_SAMPLES_FOR_LEADERBOARD = 5;

export function AdminModelStats() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
  const [data, setData] = useState<AdminModelStatsResponse | null>(null);
  const [signals, setSignals] = useState<AdminTurnSignalsResponse | null>(null);
  const [windowHours, setWindowHours] = useState(24);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('sampleCount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [contentTierFilter, setContentTierFilter] = useState<
    'all' | 'sfw' | 'moderated' | 'permissive' | 'unrestricted' | 'unknown'
  >('all');
  const [qualityTierFilter, setQualityTierFilter] = useState<
    'all' | 'narrative' | 'structured' | 'untagged'
  >('all');

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.is_admin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.is_admin) {
      loadStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, windowHours]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const [result, signalResult] = await Promise.all([getAdminModelStats(windowHours), getAdminTurnSignals(windowHours)]);
      setData(result);
      setSignals(signalResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load model stats');
    } finally {
      setLoading(false);
    }
  };

  const sortedStats = useMemo<AdminModelStat[]>(() => {
    if (!data) return [];
    let arr = [...data.stats];
    if (contentTierFilter !== 'all') {
      if (contentTierFilter === 'unknown') {
        arr = arr.filter((s) => s.contentTier === null);
      } else {
        arr = arr.filter((s) => s.contentTier === contentTierFilter);
      }
    }
    if (qualityTierFilter !== 'all') {
      if (qualityTierFilter === 'untagged') {
        arr = arr.filter((s) => s.qualityTiers.length === 0);
      } else {
        arr = arr.filter((s) => s.qualityTiers.includes(qualityTierFilter));
      }
    }
    arr.sort((a, b) => compareStats(a, b, sortKey, sortDir));
    return arr;
  }, [data, sortKey, sortDir, contentTierFilter, qualityTierFilter]);

  const leaderboard = useMemo(() => {
    if (!data) return null;
    const eligible = data.stats.filter((s) => s.sampleCount >= MIN_SAMPLES_FOR_LEADERBOARD);
    const cheapest = pickMinDefined(eligible, (s) => s.costPerKTokensMicros);
    const fastest = pickMinDefined(eligible, (s) => s.avgLatencyMs);
    const mostUsed = eligible.reduce<AdminModelStat | null>(
      (best, s) => (best === null || s.sampleCount > best.sampleCount ? s : best),
      null
    );
    return { cheapest, fastest, mostUsed };
  }, [data]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(defaultDirFor(key));
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!user?.is_admin) return null;

  return (
    <AdminLayout
      title="Model Performance"
      breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Model Performance' }]}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={windowHours}
            onChange={(e) => setWindowHours(Number(e.target.value))}
            className="bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50"
            title="Time window"
          >
            {WINDOW_OPTIONS.map((opt) => (
              <option key={opt.hours} value={opt.hours}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={contentTierFilter}
            onChange={(e) => setContentTierFilter(e.target.value as typeof contentTierFilter)}
            className="bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50 text-sm"
            title="Filter by content tier"
          >
            <option value="all">Content: all</option>
            <option value="sfw">sfw</option>
            <option value="moderated">moderated</option>
            <option value="permissive">permissive</option>
            <option value="unrestricted">unrestricted</option>
            <option value="unknown">unknown (no registry match)</option>
          </select>
          <select
            value={qualityTierFilter}
            onChange={(e) => setQualityTierFilter(e.target.value as typeof qualityTierFilter)}
            className="bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50 text-sm"
            title="Filter by quality tier"
          >
            <option value="all">Quality: all</option>
            <option value="narrative">narrative</option>
            <option value="structured">structured</option>
            <option value="untagged">untagged</option>
          </select>
          <button
            onClick={loadStats}
            className="bg-dark-700 hover:bg-dark-600 border border-dark-600 rounded px-3 py-2 text-dark-50 text-sm"
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {error && (
          <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <p className="text-sm text-dark-400">
          Aggregated from <code className="text-dark-300">api_usage</code> over the selected window.
          Latency aggregates exclude failed calls. Cost-per-1K-tokens is the apples-to-apples cost
          metric — lower wins, given the project's fidelity-per-dollar mandate.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard title="Total requests" value={data ? data.totalRequests.toLocaleString() : '—'} />
          <KpiCard
            title="Total cost"
            value={data ? formatCost(data.totalCostMicros) : '—'}
            highlight
          />
          <KpiCard title="Total tokens" value={data ? data.totalTokens.toLocaleString() : '—'} />
          <KpiCard title="Distinct models" value={data ? String(data.modelCount) : '—'} />
        </div>

        {leaderboard && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <LeaderCard
              label="Cheapest (≥5 samples)"
              row={leaderboard.cheapest}
              rightLabel="per 1K tokens"
              rightValue={(s) => formatCostPerK(s.costPerKTokensMicros)}
            />
            <LeaderCard
              label="Fastest (≥5 samples)"
              row={leaderboard.fastest}
              rightLabel="avg latency"
              rightValue={(s) => formatLatency(s.avgLatencyMs)}
            />
            <LeaderCard
              label="Most used"
              row={leaderboard.mostUsed}
              rightLabel="samples"
              rightValue={(s) => s.sampleCount.toLocaleString()}
            />
          </div>
        )}

        <div className="bg-dark-800 border border-dark-600 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-dark-600 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-dark-50">Models</h3>
            {data && (
              <div className="text-xs text-dark-400">
                Generated {new Date(data.generatedAt).toLocaleString()}
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-dark-700">
                <tr>
                  <SortableTh active={sortKey} dir={sortDir} colKey="provider" align="left" onSort={toggleSort}>
                    Provider
                  </SortableTh>
                  <SortableTh active={sortKey} dir={sortDir} colKey="model" align="left" onSort={toggleSort}>
                    Model
                  </SortableTh>
                  <th className="px-4 py-3 text-dark-300 font-medium text-left">
                    Tier
                  </th>
                  <SortableTh active={sortKey} dir={sortDir} colKey="sampleCount" align="right" onSort={toggleSort}>
                    Samples
                  </SortableTh>
                  <SortableTh active={sortKey} dir={sortDir} colKey="successRate" align="right" onSort={toggleSort}>
                    Success
                  </SortableTh>
                  <SortableTh active={sortKey} dir={sortDir} colKey="avgLatencyMs" align="right" onSort={toggleSort}>
                    Avg Latency
                  </SortableTh>
                  <SortableTh active={sortKey} dir={sortDir} colKey="maxLatencyMs" align="right" onSort={toggleSort}>
                    Max Latency
                  </SortableTh>
                  <SortableTh
                    active={sortKey}
                    dir={sortDir}
                    colKey="costPerKTokensMicros"
                    align="right"
                    onSort={toggleSort}
                  >
                    Cost / 1K tok
                  </SortableTh>
                  <SortableTh
                    active={sortKey}
                    dir={sortDir}
                    colKey="avgTokensPerRequest"
                    align="right"
                    onSort={toggleSort}
                  >
                    Avg Tokens/Req
                  </SortableTh>
                  <SortableTh
                    active={sortKey}
                    dir={sortDir}
                    colKey="totalCostMicros"
                    align="right"
                    onSort={toggleSort}
                  >
                    Total Cost
                  </SortableTh>
                  <SortableTh
                    active={sortKey}
                    dir={sortDir}
                    colKey="streamedFraction"
                    align="right"
                    onSort={toggleSort}
                  >
                    Streamed
                  </SortableTh>
                  <SortableTh active={sortKey} dir={sortDir} colKey="lastSeenAt" align="right" onSort={toggleSort}>
                    Last Seen
                  </SortableTh>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-8 text-center text-dark-400">
                      Loading…
                    </td>
                  </tr>
                ) : sortedStats.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-8 text-center text-dark-400">
                      No usage in this window.
                    </td>
                  </tr>
                ) : (
                  sortedStats.map((s) => (
                    <tr key={`${s.provider}/${s.model}`} className="border-t border-dark-600">
                      <td className="px-4 py-2 text-primary-300 capitalize">{s.provider}</td>
                      <td className="px-4 py-2 text-dark-200 font-mono text-xs">
                        <div>{s.displayName ?? s.model}</div>
                        {s.displayName && (
                          <div className="text-dark-500 text-[10px] mt-0.5">{s.model}</div>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <TierBadges
                          contentTier={s.contentTier}
                          contentCaveats={s.contentCaveats}
                          qualityTiers={s.qualityTiers}
                        />
                      </td>
                      <td className="px-4 py-2 text-dark-200 text-right">
                        {s.sampleCount.toLocaleString()}
                      </td>
                      <td className={`px-4 py-2 text-right ${successColor(s.successRate)}`}>
                        {(s.successRate * 100).toFixed(1)}%
                      </td>
                      <td className="px-4 py-2 text-dark-200 text-right">
                        {formatLatency(s.avgLatencyMs)}
                      </td>
                      <td className="px-4 py-2 text-dark-300 text-right">
                        {formatLatency(s.maxLatencyMs)}
                      </td>
                      <td className="px-4 py-2 text-green-400 text-right">
                        {formatCostPerK(s.costPerKTokensMicros)}
                      </td>
                      <td className="px-4 py-2 text-dark-300 text-right">
                        {s.avgTokensPerRequest !== null ? s.avgTokensPerRequest.toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-2 text-green-400 text-right">
                        {formatCost(s.totalCostMicros)}
                      </td>
                      <td className="px-4 py-2 text-dark-300 text-right">
                        {(s.streamedFraction * 100).toFixed(0)}%
                      </td>
                      <td className="px-4 py-2 text-dark-400 text-right text-xs">
                        {formatRelative(s.lastSeenAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <TurnSignalsTable signals={signals} />
      </div>
    </AdminLayout>
  );
}

/** Narrative signal keys shown as yes-rates (lower is better except objective pursued and second actor). */
const SIGNAL_COLUMNS: { key: string; label: string }[] = [
  { key: 'player_agency_violation', label: 'Player agency' },
  { key: 'ensemble_overreaction', label: 'Overreaction' },
  { key: 'second_actor_rendered', label: 'Second actor' },
  { key: 'objective_pursued', label: 'Objective pursued' },
  { key: 'pace_violated', label: 'Left scene early' },
  { key: 'repetition', label: 'Repetition' },
  { key: 'continuity_break', label: 'Continuity' },
  { key: 'truncated', label: 'Truncated' },
  { key: 'asked_when_not_planned', label: 'Unplanned question' },
  { key: 'user_disengaging', label: 'Disengaging' },
];

function RateCell({ rate }: { rate: AdminSignalRate | undefined }) {
  if (!rate || rate.answered === 0) return <td className="px-4 py-2 text-dark-500 text-right">—</td>;
  return (
    <td className="px-4 py-2 text-dark-200 text-right" title={`${rate.positive} of ${rate.answered} answered`}>
      {Math.round((rate.positive / rate.answered) * 100)}%
      <span className="text-dark-500 text-[10px] ml-1">/{rate.answered}</span>
    </td>
  );
}

function TurnSignalsTable({ signals }: { signals: AdminTurnSignalsResponse | null }) {
  const columns = SIGNAL_COLUMNS.filter(c => signals?.models.some(m => m.rates[c.key]?.answered));
  return (
    <div className="bg-dark-800 border border-dark-600 rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-dark-600">
        <h3 className="text-lg font-semibold text-dark-50">Turn quality signals</h3>
        <p className="text-xs text-dark-400 mt-1">
          Per serving model, from typed decisions on each accepted turn. Rates show answered counts; they are
          observational — casts, ratings, and players differ between models — so treat them as trends, not rankings.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-dark-700">
            <tr>
              <th className="px-4 py-3 text-dark-300 font-medium text-left">Kind</th>
              <th className="px-4 py-3 text-dark-300 font-medium text-left">Serving model</th>
              <th className="px-4 py-3 text-dark-300 font-medium text-right">Turns</th>
              {columns.map(c => <th key={c.key} className="px-4 py-3 text-dark-300 font-medium text-right">{c.label}</th>)}
              <th className="px-4 py-3 text-dark-300 font-medium text-right">NPC agency (0–3)</th>
              <th className="px-4 py-3 text-dark-300 font-medium text-right">Corrections</th>
              <th className="px-4 py-3 text-dark-300 font-medium text-right">Fallback</th>
              <th className="px-4 py-3 text-dark-300 font-medium text-right">Cost / accepted turn</th>
            </tr>
          </thead>
          <tbody>
            {!signals || signals.models.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 7} className="px-4 py-8 text-center text-dark-400">
                  No turn signals in this window.
                </td>
              </tr>
            ) : (
              signals.models.map(m => (
                <tr key={`${m.kind}/${m.servedModel ?? 'unknown'}`} className="border-t border-dark-600">
                  <td className="px-4 py-2 text-primary-300 capitalize">{m.kind}</td>
                  <td className="px-4 py-2 text-dark-200 font-mono text-xs">{m.servedModel ?? 'unknown'}</td>
                  <td className="px-4 py-2 text-dark-200 text-right">{m.turns}</td>
                  {columns.map(c => <RateCell key={c.key} rate={m.rates[c.key]} />)}
                  <td className="px-4 py-2 text-dark-200 text-right" title={`${m.npcAgencyAnswered} answered`}>
                    {m.meanNpcAgency === null ? '—' : m.meanNpcAgency.toFixed(2)}
                  </td>
                  <RateCell rate={m.corrections} />
                  <td className="px-4 py-2 text-dark-300 text-right">{Math.round(m.fallbackShare * 100)}%</td>
                  <td className="px-4 py-2 text-green-400 text-right">
                    {m.costPerAcceptedTurnMicros === null ? '—' : `$${(m.costPerAcceptedTurnMicros / 1_000_000).toFixed(4)}`}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TierBadges({
  contentTier,
  contentCaveats,
  qualityTiers,
}: {
  contentTier: AdminModelStat['contentTier'];
  contentCaveats: AdminModelStat['contentCaveats'];
  qualityTiers: AdminModelStat['qualityTiers'];
}) {
  // Color-code the content posture so an admin can eyeball routing safety.
  // sfw  → gray,  moderated → yellow,  permissive → orange,  unrestricted → green.
  const contentColor: Record<NonNullable<AdminModelStat['contentTier']>, string> = {
    sfw: 'bg-dark-700 text-dark-300 border-dark-600',
    moderated: 'bg-yellow-900/40 text-yellow-200 border-yellow-700/50',
    permissive: 'bg-orange-900/40 text-orange-200 border-orange-700/50',
    unrestricted: 'bg-green-900/40 text-green-200 border-green-700/50',
  };
  const qualityColor: Record<string, string> = {
    narrative: 'bg-purple-900/40 text-purple-200 border-purple-700/50',
    structured: 'bg-blue-900/40 text-blue-200 border-blue-700/50',
  };

  const caveatTitle =
    contentCaveats && contentCaveats.length > 0
      ? `Caveats:\n • ${contentCaveats.join('\n • ')}`
      : undefined;

  return (
    <div className="flex flex-wrap gap-1 items-center">
      {contentTier && (
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded border ${contentColor[contentTier]} ${
            caveatTitle ? 'cursor-help' : ''
          }`}
          title={caveatTitle}
        >
          {contentTier}
          {caveatTitle && <span className="ml-0.5">*</span>}
        </span>
      )}
      {qualityTiers.map((t) => (
        <span
          key={t}
          className={`text-[10px] px-1.5 py-0.5 rounded border ${qualityColor[t] ?? 'bg-dark-700 text-dark-300 border-dark-600'}`}
        >
          {t}
        </span>
      ))}
    </div>
  );
}

function SortableTh({
  children,
  colKey,
  active,
  dir,
  align,
  onSort,
}: {
  children: React.ReactNode;
  colKey: SortKey;
  active: SortKey;
  dir: SortDir;
  align: 'left' | 'right';
  onSort: (key: SortKey) => void;
}) {
  const isActive = active === colKey;
  const indicator = isActive ? (dir === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th
      onClick={() => onSort(colKey)}
      className={`px-4 py-3 text-dark-300 font-medium cursor-pointer select-none hover:text-dark-100 ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${isActive ? 'text-primary-300' : ''}`}
    >
      {children}
      {indicator}
    </th>
  );
}

function KpiCard({ title, value, highlight }: { title: string; value: string; highlight?: boolean }) {
  return (
    <div className="bg-dark-800 border border-dark-600 rounded-lg p-4">
      <div className="text-dark-400 text-sm mb-1">{title}</div>
      <div className={`text-2xl font-bold ${highlight ? 'text-green-400' : 'text-dark-50'}`}>
        {value}
      </div>
    </div>
  );
}

function LeaderCard({
  label,
  row,
  rightLabel,
  rightValue,
}: {
  label: string;
  row: AdminModelStat | null;
  rightLabel: string;
  rightValue: (s: AdminModelStat) => string;
}) {
  return (
    <div className="bg-dark-800 border border-dark-600 rounded-lg p-4">
      <div className="text-dark-400 text-sm mb-2">{label}</div>
      {row ? (
        <>
          <div className="text-primary-300 capitalize text-sm">{row.provider}</div>
          <div className="text-dark-50 font-mono text-sm break-all mb-2">{row.model}</div>
          <div className="flex justify-between items-baseline">
            <span className="text-dark-400 text-xs">{rightLabel}</span>
            <span className="text-green-400 font-semibold">{rightValue(row)}</span>
          </div>
        </>
      ) : (
        <div className="text-dark-500 text-sm">No qualifying data.</div>
      )}
    </div>
  );
}

function defaultDirFor(key: SortKey): SortDir {
  switch (key) {
    case 'provider':
    case 'model':
      return 'asc';
    case 'avgLatencyMs':
    case 'maxLatencyMs':
    case 'costPerKTokensMicros':
    case 'avgTokensPerRequest':
      return 'asc';
    default:
      return 'desc';
  }
}

function compareStats(a: AdminModelStat, b: AdminModelStat, key: SortKey, dir: SortDir): number {
  const av = a[key];
  const bv = b[key];
  const dirMul = dir === 'asc' ? 1 : -1;
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;
  if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dirMul;
  return String(av).localeCompare(String(bv)) * dirMul;
}

function pickMinDefined(
  rows: AdminModelStat[],
  metric: (s: AdminModelStat) => number | null
): AdminModelStat | null {
  let best: AdminModelStat | null = null;
  let bestVal: number | null = null;
  for (const s of rows) {
    const v = metric(s);
    if (v === null) continue;
    if (bestVal === null || v < bestVal) {
      best = s;
      bestVal = v;
    }
  }
  return best;
}

function formatCost(micros: number): string {
  if (!micros) return '$0.0000';
  return `$${(micros / 1_000_000).toFixed(4)}`;
}

function formatCostPerK(micros: number | null): string {
  if (micros === null) return '—';
  if (micros < 1000) return `$${(micros / 1_000_000).toFixed(6)}`;
  return `$${(micros / 1_000_000).toFixed(4)}`;
}

function formatLatency(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

function formatRelative(iso: string): string {
  if (!iso) return '—';
  const t = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z').getTime();
  if (isNaN(t)) return iso;
  const diff = Date.now() - t;
  if (diff < 60_000) return `${Math.round(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
  return `${Math.round(diff / 86_400_000)}d ago`;
}

function successColor(rate: number): string {
  if (rate >= 0.99) return 'text-green-400';
  if (rate >= 0.95) return 'text-dark-200';
  if (rate >= 0.85) return 'text-yellow-400';
  return 'text-red-400';
}
